import { TRUCK_SIZE } from '../Enums';

export type TDemurrageClaimType = 'demurrage_pickup' | 'demurrage_dropoff' | 'futile';

export type TDemurrageSide = 'carrier' | 'shipper';

/** The TruckSize values demurrage rates are matched on — sourced from the shared TRUCK_SIZE enum. */
export const TRUCK_SIZE_OPTIONS = [
	{ label: 'Rigid', value: TRUCK_SIZE.RIGID },
	{ label: 'Single/Semi', value: TRUCK_SIZE.SINGLE },
	{ label: 'A-Double', value: TRUCK_SIZE.A_DOUBLE },
	{ label: 'B-Double', value: TRUCK_SIZE.B_DOUBLE },
	{ label: 'B-Triple', value: TRUCK_SIZE.B_TRIPLE },
];

/** One eligibility-checklist rule, served by GET /demurrage/checklist for the selected load type. */
export interface TDemurrageChecklistRule {
	id: string;
	label: string;
	description: string;
}

export const DEMURRAGE_LANE_OPTIONS = [
	{ label: 'Linehaul', value: 'linehaul' },
	{ label: 'Metro', value: 'metro' },
];

export const DEMURRAGE_LOAD_OPTIONS = [
	{ label: 'FTL (full load)', value: 'ftl' },
	{ label: 'LTL (part load)', value: 'ltl' },
];

/** Charging basis (tcs_basis) options — shared by the rates admin table + the define/edit modal. */
export const DEMURRAGE_BASIS_OPTIONS = [
	{ label: 'Ofload standard', value: 'ofload' },
	{ label: 'Carrier bespoke', value: 'carrier' },
	{ label: 'Shipper bespoke', value: 'shipper' },
	{ label: 'No demurrage (Ofload absorbs)', value: 'no_demurrage' },
	{ label: 'Unknown', value: 'unknown' },
];

/** Rate structure options — shared by the rates admin table + the define/edit modal. */
export const DEMURRAGE_STRUCTURE_OPTIONS = [
	{ label: 'Lane × truck', value: 'lane_truck' },
	{ label: 'Truck type', value: 'truck_type' },
	{ label: 'Load type', value: 'load_type' },
	{ label: 'Pallet-space size', value: 'space_size' },
	{ label: 'Load + lane + truck', value: 'load_lane_truck' },
	{ label: 'Flat', value: 'flat' },
	{ label: 'Custom', value: 'custom' },
	{ label: 'None', value: 'none' },
];

export const demurrageBasisLabel = (value: string | null | undefined): string =>
	DEMURRAGE_BASIS_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '—';

export const demurrageStructureLabel = (value: string | null | undefined): string =>
	DEMURRAGE_STRUCTURE_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '—';

/**
 * One dimensioned rate tier. Mirrors the ofload_site tier shape the RateResolver matches on:
 * any constrained dimension (truck_size / lane / load / space band / pallet band) plus exactly one
 * rate basis (per 15 min, per hour or per minute) and an optional daily cap. A null/absent dimension
 * is a wildcard.
 */
export interface TDemurrageRateTier {
	label?: string;
	truck_size?: string | null;
	lane?: 'linehaul' | 'metro' | null;
	load?: 'ftl' | 'ltl' | null;
	space_min?: number | null;
	space_max?: number | null;
	pallet_min?: number | null;
	pallet_max?: number | null;
	included_minutes?: number | null;
	ratePer15?: number | null;
	ratePerHour?: number | null;
	ratePerMin?: number | null;
	cap?: number | null;
}

/**
 * A Demurrage Rate card, matching the ofload_site DemurrageRateCardResource JSON. A card is bound to
 * a carrier OR a shipper (or is the editable Ofload standard when is_ofload_standard is true). This
 * is distinct from shipment-api freight "rate cards".
 */
export interface TDemurrageRateCard {
	id: number;
	side: TDemurrageSide;
	tcs_basis: string;
	structure: string;
	is_ofload_standard: boolean;
	carrier_id: number | null;
	shipper_id: number | null;
	carrier_name: string | null;
	shipper_name: string | null;
	tiers: TDemurrageRateTier[];
	futile_chargeable: 'yes' | 'no' | 'unknown' | null;
	gps_required: boolean;
	cost_passthrough: Record<string, unknown> | null;
	demurrage_description: string | null;
	notes: string | null;
	effective_from: string | null;
	effective_to: string | null;
	version: number;
	created_at: string | null;
	updated_at: string | null;
}

/**
 * A persisted demurrage/futile calculation — the provenance record behind the claim line(s) it
 * prefills. Matches the POST /demurrage/calculations response. Amounts are GST-exclusive AUD numbers.
 */
export interface TDemurrageCalculation {
	id: number;
	reference: string | null;
	claim_type: TDemurrageClaimType;
	carrier_id: number | null;
	shipper_id: number | null;
	carrier_amount: number;
	shipper_amount: number;
	shipper_chargeable: boolean;
	margin_impact: number;
	total_wait_minutes: number | null;
}

/**
 * Provenance of the rate card that priced one side of a quote — surfaced in the calculator so the
 * operator can see what drove each figure (the Ofload standard vs a bespoke party card, the matched
 * tier, or a cost pass-through). Null when no card applied to that side.
 */
export interface TDemurrageRateSource {
	card_id: number;
	version: number | null;
	basis: string;
	structure: string;
	is_ofload_standard: boolean;
	name: string | null;
	tier: Record<string, unknown> | null;
	cost_passthrough: Record<string, unknown> | null;
}

/**
 * The live dual-sided quote from POST /demurrage/preview. The demurrage path returns every field;
 * the futile path returns only shipper_chargeable, shipper_amount and carrier_amount.
 */
export interface TDemurragePreview {
	total_wait_minutes?: number;
	carrier_approved?: boolean;
	carrier_amount: number;
	carrier_requires_override?: boolean;
	failed_rule_ids?: string[];
	shipper_chargeable: boolean;
	shipper_amount: number;
	margin_impact_amount?: number;
	carrier_rate?: TDemurrageRateSource | null;
	shipper_rate?: TDemurrageRateSource | null;
}

/**
 * The result of GET /demurrage/resolve-context: the booked timeslot + best-effort arrival, plus the
 * dimensions pre-filled from the shipment (carrier/shipper/truck_size/spaces/pallets) and whether the
 * shipper's terms make futile chargeable. lane and load are NOT resolved — they are manual.
 */
export interface TResolvedContext {
	found: boolean;
	shipment_id?: number;
	carrier_id?: number | null;
	shipper_id?: number | null;
	carrier_name?: string | null;
	shipper_name?: string | null;
	truck_size?: string | null;
	load_type?: 'ftl' | 'ltl' | null;
	spaces?: number | null;
	pallets?: number | null;
	shipper_futile_chargeable?: boolean;
	timeslot_start?: string | null;
	timeslot_end?: string | null;
	actual_arrival?: string | null;
	arrival_confidence?: 'telematics' | 'manual' | 'assumed' | 'none';
	arrival_source?: string | null;
	departure_requires_manual_entry?: boolean;
	warnings: string[];
}
